import { forwardRef, lazy, Suspense, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
  Check,
  Download,
  Layers,
  Maximize2,
  Menu,
  Minus,
  Plus,
  RotateCcw,
  TriangleAlert,
} from 'lucide-react'
import { Segmented } from './Segmented'
import { LayerRail } from './LayerRail'
import { t } from '../i18n'
import type { BoardView, GerberFile, RenderResult } from '../types'

const Board3D = lazy(() => import('./Board3D').then((module) => ({ default: module.Board3D })))

export type Phase = 'idle' | 'loading' | 'rendering' | 'ready' | 'error'
export type SaveState = 'saved' | 'saving' | 'failed'

export interface CanvasHandle {
  fit: () => void
}

interface Props {
  render: RenderResult | null
  files: GerberFile[]
  view: BoardView
  onView: (view: BoardView) => void
  name: string
  phase: Phase
  status: string
  error: string | null
  warnings: string[]
  saved: SaveState
  layerCount: number
  enabledCount: number
  onLayers: () => void
  onToggleLayer: (id: string, enabled: boolean) => void
  onDownload: () => void
  onRename: () => void
  onRetry: () => void
  onDemo: () => void
  onMobileRail: () => void
}

function svgFor(result: RenderResult, view: BoardView): string {
  // Masked SVGs force Chromium to re-layerise the whole board on every zoom
  // step (100+ ms per frame on a large panel). The flattened SVGs look the
  // same on our dark canvas and composite smoothly.
  if (view === 'bottom') return result.bottomSvgFlat
  return result.topSvgFlat
}

export const CanvasStage = forwardRef<CanvasHandle, Props>(function CanvasStage(
  {
    render,
    files,
    view,
    onView,
    name,
    phase,
    status,
    error,
    warnings,
    saved,
    layerCount,
    enabledCount,
    onLayers,
    onToggleLayer,
    onDownload,
    onRename,
    onRetry,
    onDemo,
    onMobileRail,
  },
  ref,
) {
  const stageRef = useRef<HTMLDivElement>(null)
  const svgHostRef = useRef<HTMLDivElement>(null)
  const transformRef = useRef<HTMLDivElement>(null)
  const zoomLabelRef = useRef<HTMLSpanElement>(null)
  const scaleRef = useRef(1)
  const translateRef = useRef({ x: 0, y: 0 })
  const [scanKey, setScanKey] = useState(0)
  const [fullscreen, setFullscreen] = useState(false)
  const is3d = view === '3d'

  const applyTransform = (): void => {
    const node = transformRef.current
    if (!node) return
    const { x, y } = translateRef.current
    node.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scaleRef.current})`
    if (zoomLabelRef.current) zoomLabelRef.current.textContent = `${Math.round(scaleRef.current * 100)}%`
  }

  const fit = (): void => {
    scaleRef.current = 1
    translateRef.current = { x: 0, y: 0 }
    applyTransform()
  }

  useImperativeHandle(ref, () => ({ fit }), [])

  useEffect(() => {
    fit()
  }, [view, render])

  useEffect(() => {
    const host = svgHostRef.current
    if (!host) return
    host.innerHTML = render && view !== '3d' ? svgFor(render, view) : ''
    const svg = host.querySelector('svg')
    svg?.setAttribute('aria-label', name)
    if (render && view !== '3d') setScanKey((value) => value + 1)
  }, [render, view, name])

  useEffect(() => {
    const onFullscreen = (): void => setFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', onFullscreen)
    return () => document.removeEventListener('fullscreenchange', onFullscreen)
  }, [])

  const zoomAt = (factor: number, clientX?: number, clientY?: number): void => {
    const stage = stageRef.current
    if (!stage) return
    const rect = stage.getBoundingClientRect()
    const next = Math.min(20, Math.max(0.15, scaleRef.current * factor))
    const ratio = next / scaleRef.current
    if (ratio === 1) return
    const px = (clientX ?? rect.left + rect.width / 2) - rect.left - rect.width / 2
    const py = (clientY ?? rect.top + rect.height / 2) - rect.top - rect.height / 2
    translateRef.current = {
      x: px - (px - translateRef.current.x) * ratio,
      y: py - (py - translateRef.current.y) * ratio,
    }
    scaleRef.current = next
    applyTransform()
  }

  const busy = phase === 'loading' || phase === 'rendering'

  return (
    <section className={`canvas ${is3d ? 'is-3d' : ''}`} aria-label={t('canvas.layers')}>
      <header className="canvas-head">
        <div className="canvas-head-left">
          <button className="icon-btn rail-toggle" type="button" onClick={onMobileRail} aria-label={t('rail.expand')}>
            <Menu size={16} />
          </button>
          <div className="canvas-title">
            <button className="project-name" type="button" onClick={onRename} title={t('project.rename')}>
              {name}
            </button>
            <span className={`save-state save-${saved}`}>
              {saved === 'saving' ? null : saved === 'failed' ? <TriangleAlert size={10} /> : <Check size={10} />}
              {saved === 'saving' ? t('project.saving') : saved === 'failed' ? t('project.saveFailed') : t('project.saved')}
            </span>
          </div>
          {warnings.slice(0, 2).map((warning) => (
            <span className="warn-chip" key={warning}>
              <TriangleAlert size={10} />
              {warning}
            </span>
          ))}
        </div>
        <div className="canvas-head-right">
          {is3d ? null : (
            <button className="icon-btn fit-head" type="button" onClick={fit} aria-label={t('canvas.fit')}>
              <RotateCcw size={14} />
            </button>
          )}
          <button className="tool-btn" type="button" onClick={onLayers}>
            <Layers size={15} />
            <span className="tool-label">{t('canvas.layers')}</span>
            <span className="tool-count">{enabledCount}/{layerCount}</span>
          </button>
          <button className="icon-btn" type="button" onClick={onDownload} disabled={!render} aria-label={t('canvas.download')}>
            <Download size={15} />
          </button>
          <button
            className="icon-btn"
            type="button"
            aria-label={fullscreen ? t('canvas.exitFullscreen') : t('canvas.fullscreen')}
            onClick={() => {
              const target = stageRef.current?.closest('.canvas')
              if (!document.fullscreenElement) void target?.requestFullscreen?.()
              else void document.exitFullscreen()
            }}
          >
            <Maximize2 size={15} />
          </button>
        </div>
      </header>

      <div className={`stage ${is3d ? 'is-3d' : ''}`} ref={stageRef}>
        {!is3d ? (
          <>
            <div className="stage-grid" aria-hidden="true" />
            <div className="stage-cross" aria-hidden="true" />

            <div className="board-transform" ref={transformRef}>
              <div className={`board-perspective ${view === 'perspective' ? 'is-perspective' : ''}`}>
                <div className="board-svg" ref={svgHostRef} />
              </div>
            </div>

            {render ? <span key={scanKey} className="scan-sweep" aria-hidden="true" /> : null}
          </>
        ) : render ? (
          <Suspense
            fallback={
              <div className="board3d-loading">
                <span className="spinner" />
                <strong>{t('three.loading')}</strong>
              </div>
            }
          >
            <Board3D render={render} files={files} />
          </Suspense>
        ) : null}

        <AnimatePresence>
          {busy ? (
            <motion.div
              className="status-pill"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
            >
              <span className="spinner" />
              {status}
            </motion.div>
          ) : null}
        </AnimatePresence>

        {!render && !busy ? (
          <div className="canvas-empty">
            <div className="canvas-empty-mark" aria-hidden="true">
              <BoardGlyph />
            </div>
            <h2>{error ? t('canvas.errorTitle') : t('empty.title')}</h2>
            <p>{error ?? t('empty.body')}</p>
            <div className="canvas-empty-actions">
              {error ? (
                <>
                  <button className="btn btn-primary" type="button" onClick={onRetry}>
                    {t('canvas.retry')}
                  </button>
                  <button className="btn" type="button" onClick={onDemo}>
                    {t('canvas.demo')}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        {render && !is3d ? (
          <div className="zoom-dock">
            <button className="icon-btn" type="button" onClick={() => zoomAt(0.85)} aria-label={t('canvas.zoomOut')}>
              <Minus size={14} />
            </button>
            <span className="zoom-value" ref={zoomLabelRef}>100%</span>
            <button className="icon-btn" type="button" onClick={() => zoomAt(1.18)} aria-label={t('canvas.zoomIn')}>
              <Plus size={14} />
            </button>
            <span className="dock-rule" />
            <button className="icon-btn" type="button" onClick={fit} aria-label={t('canvas.fit')}>
              <RotateCcw size={13} />
            </button>
          </div>
        ) : null}

        {files.length > 0 ? <LayerRail files={files} onToggle={onToggleLayer} /> : null}

        <div className={`view-dock ${is3d ? 'is-3d' : ''}`}>
          <Segmented
            layoutId="view-indicator"
            label={t('canvas.top')}
            value={view}
            onChange={onView}
            options={[
              { value: 'top', label: t('canvas.top') },
              { value: 'bottom', label: t('canvas.bottom') },
              { value: 'perspective', label: t('canvas.perspective') },
              { value: '3d', label: t('canvas.threeD') },
            ]}
          />
        </div>
      </div>
    </section>
  )
})

function BoardGlyph() {
  return (
    <svg viewBox="0 0 120 90" fill="none" stroke="currentColor" strokeWidth="1.1">
      <rect x="8.5" y="8.5" width="103" height="73" rx="4" />
      <path d="M18 30h20v-8M18 60h20v8M102 30H82v-8M102 60H82v8" />
      <rect x="46.5" y="31.5" width="27" height="27" rx="2" />
      <circle cx="60" cy="45" r="6" />
      <path d="M33 45h9M78 45h9M60 24v7M60 59v7" strokeDasharray="2 3" />
      <circle cx="24" cy="45" r="2.2" />
      <circle cx="96" cy="45" r="2.2" />
    </svg>
  )
}
